import * as endPoints from '../constants/EndPoints';
import { fetchMiddleware, setIndexes } from './MiddlewareActions';

/* REDUCER */

const initialState = {
  items: [],
  indexMap: {},
  selectedDeviceId: null,
  selectedDeviceBist: [],
  selectedDeviceHistory: [],
  sort: { field: null, dir: 'asc' },
};

const ACTIONS = {
  REQUEST_MOBILE_DEVICES: 'REQUEST_MOBILE_DEVICES',
  RECEIVE_MOBILE_DEVICES: 'RECEIVE_MOBILE_DEVICES',

  REQUEST_MOBILE_DEVICES_BISTS: 'REQUEST_MOBILE_DEVICES_BISTS',
  RECEIVE_MOBILE_DEVICES_BISTS: 'RECEIVE_MOBILE_DEVICES_BISTS',

  REQUEST_MOBILE_DEVICE_HISTORY: 'REQUEST_MOBILE_DEVICE_HISTORY',
  RECEIVE_MOBILE_DEVICE_HISTORY: 'RECEIVE_MOBILE_DEVICE_HISTORY',

  SET_SELECTED_MOBILE_DEVICE: 'SET_SELECTED_MOBILE_DEVICE',

  SORT_MOBILE_DEVICES: 'SORT_MOBILE_DEVICES',
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ACTIONS.REQUEST_MOBILE_DEVICES:
      return { ...state, items: [] };
    case ACTIONS.RECEIVE_MOBILE_DEVICES:
      return { ...state, items: action.payload.devices, indexMap: action.payload.indexMap };
    case ACTIONS.SET_SELECTED_MOBILE_DEVICE:
      return { ...state, selectedDeviceId: action.payload };
    case ACTIONS.REQUEST_MOBILE_DEVICES_BISTS:
      return { ...state, selectedDeviceBist: [] };
    case ACTIONS.RECEIVE_MOBILE_DEVICES_BISTS:
      return { ...state, selectedDeviceBist: action.payload };
    case ACTIONS.REQUEST_MOBILE_DEVICE_HISTORY:
      return { ...state, selectedDeviceHistory: [] };
    case ACTIONS.RECEIVE_MOBILE_DEVICE_HISTORY:
      return { ...state, selectedDeviceHistory: action.payload };
    case ACTIONS.SORT_MOBILE_DEVICES:
      return {
        ...state,
        items: action.payload.devices,
        indexMap: action.payload.indexMap,
        sort: { field: action.payload.field, dir: action.payload.dir },
      };
    default:
      return state;
  }
};

/* ACTIONS */
const getDevicesAction = (devices, indexMap) => ({
  type: devices ? ACTIONS.RECEIVE_MOBILE_DEVICES : ACTIONS.REQUEST_MOBILE_DEVICES,
  payload: { devices, indexMap },
});

export const getDeviceBistAction = (bistData) => ({
  type: bistData ? ACTIONS.RECEIVE_MOBILE_DEVICES_BISTS : ACTIONS.REQUEST_MOBILE_DEVICES_BISTS,
  payload: bistData,
});

const getDeviceHistoryAction = (history) => ({
  type: history ? ACTIONS.RECEIVE_MOBILE_DEVICE_HISTORY : ACTIONS.REQUEST_MOBILE_DEVICE_HISTORY,
  payload: history,
});

export const selectMobileDevice = (device) => (dispatch, getState) => {
  const deviceId = device && device['device_id'];
  // Closing the details panel clears the selection by calling this with an empty
  // object. Reset the device state directly instead of fetching bist/history for
  // an `undefined` id — that stray fetch repopulated the panel and made it
  // reopen with an "undefined" title.
  if (!deviceId) {
    dispatch({ type: ACTIONS.SET_SELECTED_MOBILE_DEVICE, payload: null });
    dispatch(getDeviceBistAction());
    dispatch(getDeviceHistoryAction());
    return;
  }
  // Selecting the row already happens on every tap of its cells (e.g. the state
  // dropdown). Bail out when the device is already selected so we don't refetch
  // bist/history on each tap — that refetch is what made the history flash.
  if (getState().mobile.devices.selectedDeviceId === deviceId) {
    return;
  }
  dispatch({
    type: ACTIONS.SET_SELECTED_MOBILE_DEVICE,
    payload: deviceId,
  });

  dispatch(getDeviceBist(deviceId));
  dispatch(getDeviceHistory(deviceId));
};

// Some header columnKeys don't match the underlying data field name.
const DEVICE_SORT_FIELDS = {
  ID: 'device_id',
  CreationTime: 'activation_date',
};

export const sortMobileDevices = (field, dir) => (dispatch, getState) => {
  const state = getState();
  const dataField = DEVICE_SORT_FIELDS[field] || field;
  // The customer column shows the resolved name, so sort by that, not the raw id.
  const customers = state.leaksList.customers;
  const valueOf = (device) => {
    if (dataField === 'customer_id') {
      const customer =
        customers?.items?.[customers?.indexMap?.[device.customer_id]];
      return customer ? customer.Name : device.customer_id;
    }
    return device[dataField];
  };
  const devices = [...state.mobile.devices.items];
  devices.sort((a, b) => {
    let nRc = 0;
    const firstObj = dir === 'asc' ? valueOf(a) : valueOf(b);
    const secondObj = dir === 'asc' ? valueOf(b) : valueOf(a);
    const aObj =
      typeof firstObj === 'string' ? firstObj.toLowerCase().trim() : firstObj;
    const bObj =
      typeof secondObj === 'string' ? secondObj.toLowerCase().trim() : secondObj;
    if (aObj > bObj || bObj == null) nRc = 1;
    if (aObj < bObj || aObj == null) nRc = -1;
    return nRc;
  });
  const indexMap = setIndexes(devices, 'device_id');
  dispatch({
    type: ACTIONS.SORT_MOBILE_DEVICES,
    payload: { devices, indexMap, field, dir },
  });
};

/* API */
export const getDevices = (customerId) => (dispatch, getState) => {
  dispatch(getDevicesAction());
  // Scope devices to the current customer (covers all of that customer's projects).
  const query = customerId != null ? `?customerId=${customerId}` : '';
  const url = `${endPoints.END_POINT}/mobiledevices${query}`;
  return fetchMiddleware(url, {}, getState).then((json) => {
    if (json.status) {
      // The backend isn't guaranteed to filter by customer yet, so also filter
      // here so a project only ever shows its own customer's devices.
      const devices =
        customerId != null
          ? json.data.filter(
              (d) => String(d.customer_id) === String(customerId)
            )
          : json.data;
      const indexMap = setIndexes(devices, 'device_id');
      dispatch(getDevicesAction(devices, indexMap));
    }
  });
};

const getDeviceBist = (deviceId) => (dispatch, getState) => {
  dispatch(getDeviceBistAction());
  const url = `${endPoints.END_POINT}/mobiledevices/bist/${deviceId}`;
  return fetchMiddleware(url, {}, getState).then((json) => {
    if (json.status) {
      dispatch(getDeviceBistAction(json.data));
    }
  });
};

export const getDeviceHistory = (deviceId, keepExisting = false) => (dispatch, getState) => {
  // On an in-place refresh (after a state change or new comment) skip the empty
  // REQUEST dispatch so the current rows stay on screen until the new ones land,
  // instead of flashing to an empty list. Device switches still clear first.
  if (!keepExisting) {
    dispatch(getDeviceHistoryAction());
  }
  const url = `${endPoints.END_POINT}/mobiledevices/history/${deviceId}`;
  return fetchMiddleware(url, {}, getState).then((json) => {
    if (json.status) {
      dispatch(getDeviceHistoryAction(json.data));
    }
  });
};

export const updateMobileDeviceState = (device, newState, cb) => (dispatch, getState) => {
  const deviceId = device['device_id'];
  const url = `${endPoints.END_POINT}/mobiledevices/${deviceId}`;
  return fetchMiddleware(
    url,
    {
      method: 'PUT',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ state: newState }),
    },
    getState
  ).then((json) => {
    if (json.status) {
      // Backend logs the `update_state` history row automatically.
      dispatch(getDevices(device.customer_id));
      dispatch(getDeviceHistory(deviceId, true));
    }
    if (cb) {
      cb(json.status);
    }
    return json;
  });
};

export const addMobileDeviceComment = (deviceId, comment, cb) => (dispatch, getState) => {
  const url = `${endPoints.END_POINT}/mobiledevices/history/${deviceId}`;
  return fetchMiddleware(
    url,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment }),
    },
    getState
  ).then((json) => {
    if (json.status) {
      // Backend logs the `comment` history row automatically.
      dispatch(getDeviceHistory(deviceId, true));
    }
    if (cb) {
      cb(json.status);
    }
    return json;
  });
};

export const getIQ100BReport = ({ body }) => (dispatch, getState) => {
  const url = `${endPoints.END_POINT}/mobilereport/iq100b`;
  return fetchMiddleware(`${url}`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }, getState).then((json) => {
    return json;
  });
};

export const getIQ100BReportProjectUsers = ({ body }) => (dispatch, getState) => {
  const url = `${endPoints.END_POINT}/mobilereport/iq100b-project-users`;
  return fetchMiddleware(`${url}`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  }, getState).then((json) => {
    return json;
  });
};

export const getUserDeviceReport = ({ from, to, deviceIds, customerId }) => (dispatch, getState) => {
  // from / to are "YYYY-MM" (backend accepts yyyy-mm or mm-yyyy).
  // deviceIds: explicit list of device_ids to include; omitted/empty => backend default.
  // customerId scopes the report to the selected project's customer.
  let url = `${endPoints.END_POINT}/mobiledevices/report?from=${from}&to=${to}`;
  if (customerId != null) {
    url += `&customerId=${customerId}`;
  }
  if (deviceIds && deviceIds.length) {
    url += `&devices=${deviceIds.join(',')}`;
  }
  return fetchMiddleware(url, {}, getState).then((json) => {
    return json;
  });
};

export default reducer;
